'use client';

import { c as _c } from "react/compiler-runtime";
import type { CSSProperties } from 'react';
import type { OutcomeHueName } from '@/hooks/chart/use-outcome-color';
import { outcomeHueToStepVar } from '@/lib/chart/outcome-hue';
import { cn } from '@/lib/ui/utils';
import { OutcomeLiveline } from './outcome-liveline';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface OutcomeSparklinePoint {
  /** Unix epoch in seconds (or any monotonically-increasing number). */
  time: number;
  /** TWAR-normalized share in [0, 1]. */
  value: number;
}
export interface OutcomeSparklineColorClasses {
  /** Outline tone class (e.g. `bg-outcome-blue-outline`). Unused by the primitive but kept for parity with `useOutcomeColor()`. */
  outline: string;
  /** Fill tone class — present for parity with `useOutcomeColor()`. */
  fill: string;
  /** Text tone class — present for parity with `useOutcomeColor()`. */
  text: string;
  /** Hue name — drives the saturated step-400 stroke colour the chart line uses (matches the main market-page chart). */
  hue: OutcomeHueName;
}
export interface OutcomeSparklineProps {
  /**
   * Time-series of TWAR shares in [0, 1]. Sourced from `useChartDataQuery`
   * upstream. `undefined` while the parent grid's chart-data query is still
   * loading — the dotted underlay remains visible alone. A settled series
   * with fewer than two points renders a flat line until it can show a trend.
   */
  data: ReadonlyArray<OutcomeSparklinePoint> | undefined;
  /** Latest post-mount realtime trade for this outcome, or null when none exists. */
  degenTriggerId: string | null;
  /** Viewport paired with the committed data snapshot by the grid owner. */
  timeWindowSecs?: number;
  /** Reports completion of the canvas-owned viewport transition. */
  onWindowTransitionComplete?: (windowSecs: number) => void;
  /** Outcome color slot from `useOutcomeColor()`. */
  colorClasses: OutcomeSparklineColorClasses;
  className?: string;
}

// ---------------------------------------------------------------------------
// Module-scope constants
// ---------------------------------------------------------------------------

/**
 * Local mask override for the hero sparkline's chart-surface-underlay. The
 * shared utility uses 12 % / 88 % vertical fade — too narrow to absorb a
 * 14 px dot tile in the 80 px chart slot, leaving the bottom row visibly
 * hard-cropped. We widen both fade zones (top 0-30 %, bottom 65-100 %) and
 * preserve the horizontal fade. Two gradients composited via the
 * inherited `mask-composite: intersect` keep edges feathered on both axes.
 */
const SPARKLINE_UNDERLAY_MASK_VALUE = 'linear-gradient(to bottom, transparent 0%, black 30%, black 65%, transparent 100%), linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)';
const SPARKLINE_UNDERLAY_MASK: CSSProperties = {
  maskImage: SPARKLINE_UNDERLAY_MASK_VALUE,
  WebkitMaskImage: SPARKLINE_UNDERLAY_MASK_VALUE
};

/** Below this many points, render a flat line instead of the live chart. */
const MIN_POINTS_FOR_LINE = 2;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Featured outcome chart with a dotted underlay and a flat fallback for sparse history. */
export function OutcomeSparkline(props) {
  const $ = _c(13);
  const {
    data,
    degenTriggerId,
    timeWindowSecs,
    onWindowTransitionComplete,
    colorClasses,
    className
  } = props;
  const hasHistory = data !== undefined && data.length >= MIN_POINTS_FOR_LINE;
  let t0;
  if ($[0] !== className) {
    t0 = cn("relative h-full w-full", className);
    $[0] = className;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div aria-hidden="true" className="chart-surface-underlay opacity-60" style={SPARKLINE_UNDERLAY_MASK}><div className="chart-surface-vignette absolute inset-0" /><div className="chart-surface-dots morfi-chart-dots-drift absolute inset-0" /></div>;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== colorClasses || $[4] !== data || $[5] !== degenTriggerId || $[6] !== hasHistory || $[7] !== onWindowTransitionComplete || $[8] !== timeWindowSecs) {
    t2 = data === undefined ? null : hasHistory ? <OutcomeLiveline data={data} degenTriggerId={degenTriggerId} colorClasses={colorClasses} {...timeWindowSecs !== undefined ? {
      timeWindowSecs
    } : {}} {...onWindowTransitionComplete === undefined ? {} : {
      onWindowTransitionComplete
    }} /> : <div aria-hidden="true" className="pointer-events-none absolute top-1/2 right-5 left-1 border-t" data-testid="sparkline-flat-line" style={{
      borderColor: outcomeHueToStepVar(colorClasses.hue, 400)
    }} />;
    $[3] = colorClasses;
    $[4] = data;
    $[5] = degenTriggerId;
    $[6] = hasHistory;
    $[7] = onWindowTransitionComplete;
    $[8] = timeWindowSecs;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t0 || $[11] !== t2) {
    t3 = <div className={t0} data-testid="outcome-sparkline">{t1}{t2}</div>;
    $[10] = t0;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}